import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TranslationRequestsTab from "../../components/licences/TranslationRequestsTab";
import {
  getLicence,
  updateLicence,
  addNote,
  createDocument,
  lockDocument,
  unlockDocument,
  uploadFileAndGetUrl,
} from "../../services/licences";
import {
  getCOA,
  listTranslations as listCOATranslations,
} from "../../services/coa";
import toast from "react-hot-toast";
import LocalizationModal from "../../components/modals/LocalizationModal";
import AddSupportDocumentModal from "../../components/ui/AddSupportDocumentModal";
import { formatTranslationsRequestedForDisplay } from "../../utils/formatTranslationSelectionLabel";

const ORGANIZATION_TYPES = [
  "Academia – Non-commercial",
  "Academia – Commercially funded",
  "Publicly funded healthcare provider",
  "Privately funded healthcare provider",
  "Publisher – Educational use",
  "Individual Clinical Practice – Not for profit",
  "Individual Clinical Practice – For profit",
  "Nutraceutical Company",
  "Contract Research Organisation (CRO)",
  "Commercial Research – Pharma or medical device company",
  // "IT Company – Access only (No sublicensing)",
  // "IT Company – Full rights",
  "Translator / Translation Company",
];

const EditLicenseRequest = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isTranslationModalOpen, setIsTranslationModalOpen] = useState(false);
  const [coaTranslations, setCoaTranslations] = useState([]);
  const [coa, setCoa] = useState(null);
  const [licenceData, setLicenceData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [activeTab, setActiveTab] = useState("form"); // 'form' | 'translations'
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [documentLockToggling, setDocumentLockToggling] = useState(null); // docId while lock/unlock in progress
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [formData, setFormData] = useState({
    organizationType: "",
    intendedUse: "",
    translationsRequested: [],
    customTranslationsText: "",
    eCOA: false,
    numUsers: "",
    numUses: "",
    pqTrustNumber: "",
    startDate: "",
    endDate: "",
    territory: [],
    wantOriginalTranslation: false,
    contact: {
      fullName: "",
      jobTitle: "",
      email: "",
      phone: "",
    },
    org: {
      name: "",
      address1: "",
      address2: "",
      city: "",
      region: "",
      postcode: "",
      country: "",
      vatNumber: "",
    },
  });

  useEffect(() => {
    loadLicence();
  }, [id]);

  const loadLicence = async () => {
    try {
      setLoading(true);
      const response = await getLicence(id);
      if (response.data) {
        const data = response.data;
        setLicenceData(data);

        // Load COA and translations
        if (data.coaId?._id || data.coaId) {
          const coaId = data.coaId._id || data.coaId;
          await loadCOAData(coaId);
        }

        // Parse translationsRequested - could be array of strings or objects
        let translationsRequested = [];
        if (Array.isArray(data.translationsRequested)) {
          translationsRequested = data.translationsRequested.map((t) => {
            if (typeof t === "string") {
              // Format: "Country - Language"
              return t;
            } else if (t.country && t.language) {
              return `${t.country} - ${t.language}`;
            }
            return t;
          });
        }

        setFormData({
          organizationType: data.organizationType || "",
          intendedUse: data.intendedUse || "",
          translationsRequested: translationsRequested,
          customTranslationsText: data.customTranslationsText || "",
          eCOA: data.eCOA || false,
          numUsers: data.numUsers || "",
          numUses: data.numUses || "",
          pqTrustNumber: data.pqTrustNumber || "",
          startDate: data.startDate
            ? new Date(data.startDate).toISOString().split("T")[0]
            : "",
          endDate: data.endDate
            ? new Date(data.endDate).toISOString().split("T")[0]
            : "",
          territory: Array.isArray(data.territory)
            ? data.territory
            : data.territory
              ? [data.territory]
              : [],
          wantOriginalTranslation: data.wantOriginalTranslation || false,
          contact: {
            fullName: data.contact?.fullName || "",
            jobTitle: data.contact?.jobTitle || "",
            email: data.contact?.email || "",
            phone: data.contact?.phone || "",
          },
          org: {
            name: data.org?.name || "",
            address1: data.org?.address1 || "",
            address2: data.org?.address2 || "",
            city: data.org?.city || "",
            region: data.org?.region || "",
            postcode: data.org?.postcode || "",
            country: data.org?.country || "",
            vatNumber: data.org?.vatNumber || "",
          },
        });
        
        // Load notes
        setNotes(data.notes || []);
      }
    } catch (error) {
      console.error("Failed to load licence:", error);
      toast.error("Failed to load licence details");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const loadCOAData = async (coaId) => {
    try {
      const [coaResponse, translationsResponse] = await Promise.all([
        getCOA(coaId).catch(() => ({ data: null })),
        listCOATranslations(coaId).catch(() => ({ data: [] })),
      ]);

      if (coaResponse.data) {
        setCoa(coaResponse.data);
      }

      if (translationsResponse.data) {
        setCoaTranslations(translationsResponse.data);
      }
    } catch (error) {
      console.error("Failed to load COA data:", error);
    }
  };

  const handleTranslationModalSave = ({
    selectedLocales,
    customRequest,
    wantOriginalTranslation,
    territory,
  }) => {
    setFormData((prev) => ({
      ...prev,
      translationsRequested: selectedLocales,
      customTranslationsText: customRequest || prev.customTranslationsText,
      wantOriginalTranslation: wantOriginalTranslation,
      territory: territory ? [territory] : prev.territory,
    }));
    setIsTranslationModalOpen(false);
  };

  const handleOpenTranslationModal = async () => {
    if (!licenceData?.coaId?._id && !licenceData?.coaId) {
      toast.error("COA information not available");
      return;
    }

    const coaId = licenceData.coaId._id || licenceData.coaId;

    // Load translations if not already loaded
    if (coaTranslations.length === 0) {
      await loadCOAData(coaId);
    }

    setIsTranslationModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);

      // Format translationsRequested for API - backend expects array of strings
      const formattedTranslations = formData.translationsRequested.map((t) => {
        if (typeof t === "string") {
          return t; // Already in correct format "Country - Language"
        } else if (t && typeof t === "object" && t.country && t.language) {
          return `${t.country} - ${t.language}`;
        }
        return t;
      });

      // Clean up the update data - only include fields that are defined and match schema
      const updateData = {};

      // Only add fields that have values
      if (formData.organizationType)
        updateData.organizationType = formData.organizationType;
      if (formData.intendedUse) updateData.intendedUse = formData.intendedUse;
      // Always include translations payload so admin can clear them too
      updateData.translationsRequested = formattedTranslations;
      updateData.customTranslationsText = formData.customTranslationsText || "";
      updateData.eCOA = formData.eCOA;
      updateData.wantOriginalTranslation = !!formData.wantOriginalTranslation;
      if (formData.numUsers) updateData.numUsers = parseInt(formData.numUsers);
      if (formData.numUses) updateData.numUses = parseInt(formData.numUses);
      if (formData.pqTrustNumber)
        updateData.pqTrustNumber = formData.pqTrustNumber;
      if (formData.startDate) updateData.startDate = formData.startDate;
      if (formData.endDate) updateData.endDate = formData.endDate;
      // Always include territory so it can be cleared
      updateData.territory = Array.isArray(formData.territory) ? formData.territory : [];

      // Build contact object only if it has at least one field
      const contactFields = {};
      if (formData.contact.fullName)
        contactFields.fullName = formData.contact.fullName;
      if (formData.contact.jobTitle)
        contactFields.jobTitle = formData.contact.jobTitle;
      if (formData.contact.email) contactFields.email = formData.contact.email;
      if (formData.contact.phone) contactFields.phone = formData.contact.phone;
      if (Object.keys(contactFields).length > 0) {
        updateData.contact = contactFields;
      }

      // Build org object only if it has at least one field
      const orgFields = {};
      if (formData.org.name) orgFields.name = formData.org.name;
      if (formData.org.address1) orgFields.address1 = formData.org.address1;
      if (formData.org.address2) orgFields.address2 = formData.org.address2;
      if (formData.org.city) orgFields.city = formData.org.city;
      if (formData.org.region) orgFields.region = formData.org.region;
      if (formData.org.postcode) orgFields.postcode = formData.org.postcode;
      if (formData.org.country) orgFields.country = formData.org.country;
      if (formData.org.vatNumber) orgFields.vatNumber = formData.org.vatNumber;
      if (Object.keys(orgFields).length > 0) {
        updateData.org = orgFields;
      }

      await updateLicence(id, updateData);
      toast.success("Licence request updated successfully");
      navigate(`/licences/${id}`);
    } catch (error) {
      console.error("Failed to update licence:", error);
      toast.error(error.message || "Failed to update licence request");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === "checkbox" ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const getSelectedTranslationsDisplay = () => {
    if (
      formData.translationsRequested.length === 0 &&
      !formData.customTranslationsText
    ) {
      return "No translations selected";
    }

    const translations = formatTranslationsRequestedForDisplay(
      formData.translationsRequested,
      coaTranslations
    );

    if (formData.customTranslationsText) {
      return translations
        ? `${translations} + Custom request`
        : "Custom request only";
    }

    return translations || "No translations selected";
  };

  const selectedDocuments = licenceData?.supportDocumentsSelected || [];

  const handleAddSupportDocument = async (newDoc) => {
    if (!id) return;
    try {
      setUploadingDocument(true);
      let fileUrl = newDoc.fileUrl;
      if (newDoc.file && !fileUrl) {
        fileUrl = await uploadFileAndGetUrl(newDoc.file);
      }
      const feeValue =
        newDoc.fee === "£0.00" || !newDoc.fee
          ? 0
          : parseFloat(String(newDoc.fee).replace(/£/g, "").replace(/,/g, "")) || 0;
      await createDocument(id, {
        name: newDoc.name,
        detail: newDoc.detail || "",
        fee: feeValue,
        fileUrl,
        isLocked: newDoc.isLocked !== undefined ? newDoc.isLocked : true,
      });
      toast.success("Support document added");
      setIsAddDocumentModalOpen(false);
      await loadLicence();
    } catch (err) {
      console.error("Failed to add support document:", err);
      toast.error(err.message || "Failed to add support document");
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleLockUnlock = async (docId, currentlyLocked) => {
    if (!id || !docId) return;
    try {
      setDocumentLockToggling(docId);
      if (currentlyLocked) {
        await unlockDocument(id, docId);
        toast.success("Document unlocked");
      } else {
        await lockDocument(id, docId);
        toast.success("Document locked");
      }
      await loadLicence();
    } catch (err) {
      console.error("Lock/unlock failed:", err);
      toast.error(err.message || "Failed to update lock status");
    } finally {
      setDocumentLockToggling(null);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error("Please enter a note");
      return;
    }

    try {
      setAddingNote(true);
      const response = await addNote(id, newNote.trim());
      if (response.success && response.data) {
        // Add the new note to the notes array
        setNotes((prev) => [...prev, response.data.note]);
        setNewNote("");
        toast.success("Note added successfully");
      } else {
        throw new Error(response.error || "Failed to add note");
      }
    } catch (error) {
      console.error("Failed to add note:", error);
      toast.error(error.message || "Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const formatNoteDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="bg-[#fafafa] min-h-screen p-6">
        <div className="max-w-4xl mx-auto bg-white rounded-lg p-8">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#fafafa] min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/requested-licences")}
                className="px-4 py-2 h-[40px] border border-[#e7ebe9] bg-white text-[#485b54] rounded-[8px] text-[14px] font-medium hover:bg-[#F8F9FA] transition-colors flex items-center gap-2"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 12L6 8L10 4" />
                </svg>
                Back
              </button>
              <h1 className="text-2xl font-semibold text-[#222f2a]">
                Edit Licence Request
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/licences/${id}`)}
                className="px-6 py-2 h-[40px] border border-[#e7ebe9] bg-white text-[#485b54] rounded-[8px] text-[14px] font-medium hover:bg-[#F8F9FA] transition-colors"
              >
                View
              </button>
              <button
                onClick={() => navigate(`/licences/${id}`)}
                className="px-4 py-2 text-sm text-[#73827c] hover:text-[#222f2a]"
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white border border-[#f2f2f2] rounded-[16px]">
            <div className="bg-[#f2f2f2] p-[6px] border-b border-[#f2f2f2]">
              <div className="flex gap-0">
                {["form", "translations"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 px-[16px] py-[6px] text-[14px] font-medium rounded-[8px] transition-colors ${
                      activeTab === tab
                        ? "bg-emerald-400 text-white"
                        : "text-emerald-400 hover:bg-white"
                    }`}
                  >
                    {tab === "form" ? "Form" : "Translations"}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {activeTab === "form" ? (
        <div className="flex gap-6">
          {/* Main Form - Left Side */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm border border-[#f2f2f2] p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
            {/* Organization Type */}
            <div>
              <label className="block text-sm font-medium text-[#485b54] mb-2">
                Organisation Type
              </label>
              <div className="relative">
                <select
                  name="organizationType"
                  value={formData.organizationType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-[#E5E7EB] rounded-lg appearance-none text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                >
                  <option value="">Select organisation type</option>
                  {ORGANIZATION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-[#73827C]">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Intended Use */}
            <div>
              <label className="block text-sm font-medium text-[#485b54] mb-2">
                Intended Use
              </label>
              <textarea
                name="intendedUse"
                value={formData.intendedUse}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191] resize-none"
              />
            </div>

            {/* eCOA - Radio Buttons */}
            <div>
              <label className="block text-sm font-medium text-[#485b54] mb-2">
                eCOA
              </label>
              <div className="flex gap-6">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="eCOA"
                    checked={formData.eCOA === true}
                    onChange={() => setFormData({ ...formData, eCOA: true })}
                    className="w-4 h-4 text-[#40C191] border-[#E5E7EB] focus:ring-[#40C191]"
                  />
                  <span className="ml-2 text-[#485B54]">Yes</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="eCOA"
                    checked={formData.eCOA === false}
                    onChange={() => setFormData({ ...formData, eCOA: false })}
                    className="w-4 h-4 text-[#40C191] border-[#E5E7EB] focus:ring-[#40C191]"
                  />
                  <span className="ml-2 text-[#485B54]">No</span>
                </label>
              </div>
            </div>

            {/* Translations */}
            <div>
              <label className="block text-sm font-medium text-[#485b54] mb-2">
                Translations
              </label>
              <div className="space-y-2">
                <div className="px-4 py-3 border border-[#E5E7EB] rounded-lg bg-[#fafafa]">
                  <p className="text-sm text-[#485B54]">
                    {getSelectedTranslationsDisplay()}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleOpenTranslationModal}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#40C191] hover:bg-[#35A47C] rounded-lg"
                >
                  Select Translations
                </button>
              </div>
            </div>

            {/* Support documents (user-requested + admin upload, lock/unlock) */}
            <div>
              <label className="block text-sm font-medium text-[#485b54] mb-2">
                Support documents
              </label>
              <div className="space-y-2">
                {selectedDocuments.length === 0 ? (
                  <p className="text-sm text-[#73827c] py-2">No support documents for this request yet.</p>
                ) : (
                  <ul className="border border-[#E5E7EB] rounded-lg divide-y divide-[#E5E7EB] overflow-hidden">
                    {selectedDocuments.map((doc) => (
                      <li
                        key={doc._id}
                        className="flex items-center justify-between gap-3 px-4 py-3 bg-white hover:bg-[#fafafa]  "
                      >
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-[#222f2a]">{doc.name || "Unnamed"}</span>
                          {doc.detail && (
                            <p className="text-sm text-[#73827c] truncate mt-0.5">
                              {doc.detail?.length > 20 ? doc.detail.slice(0, 20) + "…" : doc.detail}
                            </p>
                          )}
                          {doc.fee != null && (
                            <span className="text-xs text-[#73827c]">
                              {typeof doc.fee === "number" ? `£${doc.fee}` : doc.fee}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {doc.locked ? (
                            <span className="text-xs text-amber-600 font-medium">Locked</span>
                          ) : (
                            <span className="text-xs text-[#73827c]">Unlocked</span>
                          )}
                          <button
                            type="button"
                            disabled={documentLockToggling === doc._id}
                            onClick={() => handleLockUnlock(doc._id, doc.locked)}
                            className="px-3 py-1.5 text-sm font-medium rounded-lg border border-[#E5E7EB] bg-white text-[#485b54] hover:bg-[#F8F9FA] disabled:opacity-50"
                          >
                            {documentLockToggling === doc._id
                              ? "…"
                              : doc.locked
                                ? "Unlock"
                                : "Lock"}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  type="button"
                  onClick={() => setIsAddDocumentModalOpen(true)}
                  disabled={uploadingDocument}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#40C191] hover:bg-[#35A47C] rounded-lg disabled:opacity-50"
                >
                  {uploadingDocument ? "Uploading…" : "Upload support document"}
                </button>
              </div>
            </div>

            {/* Number of Users */}
            <div>
              <label className="block text-sm font-medium text-[#485b54] mb-2">
                Number of Users
              </label>
              <input
                type="number"
                name="numUsers"
                value={formData.numUsers}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
              />
            </div>

            {/* Number of Uses */}
            <div>
              <label className="block text-sm font-medium text-[#485b54] mb-2">
                Number of Uses
              </label>
              <input
                type="number"
                name="numUses"
                value={formData.numUses}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
              />
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-[#485b54] mb-2">
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-[#485b54] mb-2">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
              />
            </div>

            {/* Contact Information */}
            <div className="border-t border-[#ebebeb] pt-6">
              <h3 className="text-lg font-semibold text-[#222f2a] mb-4">
                Contact Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="contact.fullName"
                    value={formData.contact.fullName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Job Title
                  </label>
                  <input
                    type="text"
                    name="contact.jobTitle"
                    value={formData.contact.jobTitle}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="contact.email"
                    value={formData.contact.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Phone
                  </label>
                  <input
                    type="text"
                    name="contact.phone"
                    value={formData.contact.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                  />
                </div>
              </div>
            </div>

            {/* Organization Details */}
            <div className="border-t border-[#ebebeb] pt-6">
              <h3 className="text-lg font-semibold text-[#222f2a] mb-4">
                Organisation Details
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Organisation Name
                  </label>
                  <input
                    type="text"
                    name="org.name"
                    value={formData.org.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Address Line 1
                  </label>
                  <input
                    type="text"
                    name="org.address1"
                    value={formData.org.address1}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Address Line 2
                  </label>
                  <input
                    type="text"
                    name="org.address2"
                    value={formData.org.address2}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#485b54] mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      name="org.city"
                      value={formData.org.city}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#485b54] mb-2">
                      Region
                    </label>
                    <input
                      type="text"
                      name="org.region"
                      value={formData.org.region}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#485b54] mb-2">
                      Postcode
                    </label>
                    <input
                      type="text"
                      name="org.postcode"
                      value={formData.org.postcode}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#485b54] mb-2">
                      Country
                    </label>
                    <input
                      type="text"
                      name="org.country"
                      value={formData.org.country}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    VAT Number
                  </label>
                  <input
                    type="text"
                    name="org.vatNumber"
                    value={formData.org.vatNumber}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                  />
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-4 pt-6 border-t border-[#ebebeb]">
              <button
                type="button"
                onClick={() => navigate(`/licences/${id}`)}
                className="px-6 py-2 text-sm font-medium text-[#73827c] hover:text-[#222f2a]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 text-sm font-medium text-white bg-[#40C191] hover:bg-[#35A47C] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </div>

          {/* Notes Section - Right Side */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-[#f2f2f2] p-6 sticky top-6">
              <h2 className="text-lg font-semibold text-[#222f2a] mb-4">
                Notes
              </h2>
              
              {/* Add Note Form */}
              <div className="mb-6">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note..."
                  rows={4}
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] text-sm focus:outline-none focus:ring-1 focus:ring-[#40C191] resize-none mb-3"
                />
                <button
                  type="button"
                  onClick={handleAddNote}
                  disabled={addingNote || !newNote.trim()}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-[#40C191] hover:bg-[#35A47C] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingNote ? "Adding..." : "Add Note"}
                </button>
              </div>

              {/* Notes List */}
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {notes.length === 0 ? (
                  <p className="text-sm text-[#73827c] text-center py-4">
                    No notes yet
                  </p>
                ) : (
                  notes
                    .slice()
                    .reverse()
                    .map((note, index) => (
                      <div
                        key={index}
                        className="border border-[#E5E7EB] rounded-lg p-4 bg-[#fafafa]"
                      >
                        <p className="text-sm text-[#485B54] mb-2 whitespace-pre-wrap">
                          {note.note}
                        </p>
                        <div className="flex items-center justify-between text-xs text-[#73827c]">
                          <span>
                            {note.addedBy?.fullName || "Unknown User"}
                          </span>
                          <span>{formatNoteDate(note.addedAt)}</span>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        </div>
        ) : (
        /* Translations Tab */
        <div className="bg-white border border-[#f2f2f2] rounded-[16px] p-6">
          <TranslationRequestsTab licenceId={id} isEditable={true} />
        </div>
        )}
      </div>

      {/* Translation Modal */}
      {coa && (
        <LocalizationModal
          isOpen={isTranslationModalOpen}
          onClose={() => setIsTranslationModalOpen(false)}
          onSave={handleTranslationModalSave}
          translations={coaTranslations}
          coa={coa}
          wantOriginalTranslation={formData.wantOriginalTranslation}
          initialTerritory={formData.territory?.[0] || ""}
          initialSelectedLocales={formData.translationsRequested || []}
          initialCustomRequest={formData.customTranslationsText || ""}
        />
      )}

      {/* Support document upload modal */}
      <AddSupportDocumentModal
        isOpen={isAddDocumentModalOpen}
        onClose={() => setIsAddDocumentModalOpen(false)}
        onAdd={handleAddSupportDocument}
      />
    </div>
  );
};

export default EditLicenseRequest;
